import { z } from "zod";
import { BOARD_SNAPSHOT_FORMAT } from "./board.ts";
import { Lesson } from "./room.ts";
import { EMPTY_BACKGROUND, SlideSnapshot } from "./slides.ts";

/**
 * Заготовки уроков: материал, собранный заранее и не привязанный к ученику.
 *
 * Две вещи, из которых следует всё остальное.
 *
 * 1. **Формат слайдов тот же, что у снимка доски формата 2.** Совпадение не
 *    косметическое: благодаря ему один и тот же компонент рисует комнату,
 *    архив и редактор, а перенос слайдов в урок это копирование, а не
 *    преобразование. Поэтому здесь переиспользуется `SlideSnapshot`, а не
 *    заводится похожий тип рядом
 * 2. **Заготовка живёт обычным JSON в Postgres, а не в CRDT.** Над ней
 *    работает один человек, синхронизировать нечего, а лишний Y.Doc это
 *    лишние надгробия (раздел 5.3 CLAUDE.md)
 *
 * Урок получает КОПИЮ слайдов, а не ссылку: правка заготовки не должна
 * переписывать прошлые уроки, иначе архив трёхмесячной давности покажет не то,
 * что было на занятии.
 */

/**
 * Версия формата слайдов в заготовке. Ровно та же нумерация, что у снимка
 * доски: формат один, и две независимые шкалы версий для него означали бы, что
 * однажды они разойдутся, а читатель этого не заметит.
 */
export const PLAN_SLIDES_FORMAT = BOARD_SNAPSHOT_FORMAT;

/**
 * Сколько слайдов в заготовку можно ДОБАВИТЬ. Не про базу, а про человека:
 * заготовка на сотню слайдов это уже курс, и листать его полосой снизу
 * невозможно. Упрёмся, заведём папки и разбиение.
 *
 * ⚠️ С шага 1б (`docs/pdf-append-design.md`, 3.4 п. 5) это потолок ДОБАВЛЕНИЯ, а
 * не хранимого. Держат его те, кто сам заводит слайды: вкладка и дописывание
 * страниц PDF. Сервер на сохранении его не проверяет: перебазирование поверх
 * дописанного сохраняет всё (решение владельца 11.10.2026), и хранимое может
 * временно быть больше 60, до `PLAN_HARD_MAX_SLIDES`.
 */
export const PLAN_MAX_SLIDES = 60;

/**
 * Жёсткий предел хранимого: столько слайдов сервер записывает на любом пути
 * записи, и не больше. Честная вкладка до него не доходит (лемма 3.4 п. 5:
 * не больше 119), поэтому упирается в него только клиент мимо вкладки.
 */
export const PLAN_HARD_MAX_SLIDES = 120;

/**
 * Предел веса для ДОПИСЫВАНИЯ: хранимое плюс дописанное не тяжелее (3.4 п. 5).
 * Мера одна, `slidesWireBytes`, байты UTF-8 по проводу.
 *
 * ⚠️ Лимитом тела сохранения и чтения засева это число больше не служит: у
 * них свои пределы, БОЛЬШЕ этого (`PLAN_SAVE_BODY_MAX` и `PLAN_SEED_BODY_MAX`,
 * оба считаются от `PLAN_STORED_MAX_BYTES`). Перебазирование делает
 * хранимое тяжелее присланного, и одна общая константа резала бы засев
 * заготовки, которую только что сохранили.
 */
export const PLAN_SLIDES_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Предел хранимого по СКОМПИЛИРОВАННЫМ слайдам, на быстром пути сохранения и по
 * итогу перебазирования; отказ `409 plan_too_heavy`. Запас в 512 КБ над
 * `PLAN_SLIDES_MAX_BYTES` держит вкладку, у которой несохранённый прирост лёг
 * поверх дописанного: вес вкладка не знает, её предел живёт здесь.
 */
export const PLAN_STORED_MAX_BYTES = PLAN_SLIDES_MAX_BYTES + 512 * 1024;

/**
 * Лимит тела сохранения (`bodyLimit`). 64 КБ это конверт тела; источники в
 * этот запас гарантированно не помещаются, и заготовка с большим числом
 * заданий у предела упрётся в `413` раньше `409`, как и раньше.
 */
export const PLAN_SAVE_BODY_MAX = PLAN_STORED_MAX_BYTES + 64 * 1024;

/**
 * Лимит тела засева в collab. Хранимое по построению не тяжелее
 * `PLAN_STORED_MAX_BYTES`, а урок перекомпилирует элементы заново, и рост на
 * поле в каждом из 2 880 элементов не должен ронять засев: «сохранилось,
 * значит засевается». Запас большой, потому что ручка внутренняя и предел там
 * только охрана памяти.
 */
export const PLAN_SEED_BODY_MAX = PLAN_STORED_MAX_BYTES + 1024 * 1024;

export const PlanId = z.string().uuid();
export type PlanId = z.infer<typeof PlanId>;

export const PlanTitle = z.string().trim().min(1).max(200);

/**
 * Слайды заготовки на входах записи и засева. Пустого списка не бывает:
 * заготовка без слайдов это экран, на котором нечего показать и нечем это
 * исправить, ровно как урок без слайдов.
 *
 * ⚠️ Потолок здесь `PLAN_HARD_MAX_SLIDES`, а не 60: заготовка после
 * перебазирования бывает больше 60 (3.4), и засев с потолком 60 не открыл бы
 * доску занятия по ней вовсе. Схемы с потолком 60 на входах больше нет.
 */
export const PlanSlidesWire = z.array(SlideSnapshot).min(1).max(PLAN_HARD_MAX_SLIDES);
export type PlanSlidesWire = z.infer<typeof PlanSlidesWire>;

/** Строка списка. Слайдов здесь нет: список не должен тянуть мегабайты рисунков. */
export const PlanSummary = z.object({
  id: PlanId,
  title: z.string(),
  slideCount: z.number().int().nonnegative(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
  /** Не null означает «в корзине». Хранится 30 суток, как уроки и файлы. */
  deletedAt: z.number().int().nullable(),
  /**
   * Версия слайдов. Растёт на каждую запись, кто бы её ни делал, и на ней, а не
   * на флаге занятости, держится корректность одновременных записей: редактор
   * шлёт весь массив целиком, и без версии вкладка со старым состоянием тихо
   * затирает чужую запись (раздел 15 docs/pptx-import-design.md).
   *
   * `default(0)` держит совместимость с сервером, который поля ещё не отдаёт.
   */
  revision: z.number().int().nonnegative().default(0),
  /**
   * Сколько живых уроков собрано по этой заготовке.
   *
   * ⚠️ `null` означает «в этом ответе не считали», а НЕ «уроков нет». Считает
   * его только тот, кто отдаёт заготовку целиком или списком; ответы на
   * переименование, дублирование и автосохранение обходятся без него, потому
   * что там он никому не нужен, а лишний агрегат на каждый штрих пера платить
   * незачем. Ноль и null поэтому обязаны различаться: «уроков нет» это факт, и
   * его показывают, а «не считали» это отсутствие данных, и показывать там
   * нечего.
   *
   * `default(null)` держит совместимость с сервером, который поля ещё не отдаёт.
   */
  lessonCount: z.number().int().nonnegative().nullable().default(null),
});
export type PlanSummary = z.infer<typeof PlanSummary>;

/**
 * Источники упражнений заготовки: по id упражнения то, что набрал
 * преподаватель, вместе с ответами.
 *
 * ⚠️ Тип здесь нарочно неразобранный. Настоящая схема это `ExerciseSource` из
 * `@edu/shared/exercises`, и сослаться на неё отсюда нельзя: этот модуль
 * реэкспортируется из `index.ts`, то есть уехал бы в стартовый чанк вместе с
 * ответами. Разбирают эти данные ровно двое, API и редактор заготовки, и оба
 * импортируют схему по явному подпути.
 *
 * Наружу источники отдаются только владельцу заготовки, вместе с самой
 * заготовкой: ученик её не видит вовсе.
 */
export const PlanSources = z.record(z.string(), z.unknown());
export type PlanSources = z.infer<typeof PlanSources>;

/**
 * Что умеет писатель заготовки.
 *
 * ⚠️ Это НЕ версия формата слайдов и она НЕ равна `PLAN_SLIDES_FORMAT`.
 * Сдвинуть формат нельзя: он общий со снимком доски, `BoardSnapshotResponse`
 * знает только 1 и 2, а сравнение хранимого формата с текущей константой в
 * выдаче архива отправило бы все существующие снимки в отказ прямо в момент
 * выката. Поэтому возможности писателя это отдельное число, живущее ровно в
 * одном месте: в guard записи заготовки.
 *
 * 3 означает «эта вкладка умеет второй слой и не потеряет его при
 * пересохранении». Вкладка со старым бандлом числа не шлёт вовсе, и вот чем
 * это опасно: у `SlideSnapshot` нет `.strict()`, значит её zod молча
 * отбрасывает незнакомое поле, а редактор шлёт ВЕСЬ массив слайдов. Первый же
 * штрих в старой вкладке сохранил бы заготовку без упражнений, с корректной
 * ревизией и с ответом 200. Ревизия этого не ловит по построению: она
 * защищает от записи поверх чужой правки, а здесь правка своя и
 * последовательная.
 *
 * 4 означает «эта вкладка знает `ordered` и `origin` у `choice`»
 * (`docs/exercise-extraction-design.md`, решение 13). Вкладка версии 3 режет
 * их тем же путём: `ExerciseSource.safeParse` в редакторе без `.strict()`, а
 * источники уезжают на сервер целиком. Поэтому писатель живёт в трёх
 * состояниях, и guard в `savePlanSlides` различает все три: без версии только
 * заготовка без упражнений, 3 всё, кроме заготовки с `choice`, у которого есть
 * `ordered` или `origin`, 4 и выше всё.
 *
 * ⚠️ Пороги названы отдельно от `PLAN_WRITER_FORMAT`: guard сравнивает с
 * `WRITER_ORDERED`, а не с текущей версией сборки, иначе подъём сборки до 5
 * перевёл бы вкладку 4 в «режет `ordered`».
 */
export const WRITER_ORDERED = 4;

/**
 * 5 означает «эта вкладка вливает дописанное из ответа сохранения»
 * (`appendedSlides`, `docs/pdf-append-design.md`, 3.4 п. 4). Писатель ниже
 * этого ставит ревизию по ответу и следующим сохранением стёр бы дописанные
 * страницы, поэтому на заготовке с непустым журналом дописываний он получает
 * `plan_writer_outdated` на любом пути, быстрый включительно.
 */
export const WRITER_REBASE = 5;

/**
 * 6 означает «эта вкладка знает привязку страницы и признаки ключа у
 * источника»: `page`, `keyFrom`, `refersToPage`, `review`
 * (`docs/scan-extraction-design.md`, решения 16 и 22). Вкладка ниже режет их
 * тем же путём, что вкладка 3 резала `ordered`: схема источника без
 * `.strict()` отбрасывает незнакомое поле молча, а источники уезжают на сервер
 * целиком.
 *
 * ⚠️ `PLAN_WRITER_FORMAT` поднят до этого числа шагом веба (раздел 17, пункт
 * 5) тем же слиянием, что вкладка, которая эти поля ставит (раскладка скана и
 * текстового пути) и не теряет при правке задания: панели свойств правят
 * источник через `editSource` (`apps/web/.../source-edit.ts`), сохранность
 * четырёх полей стережёт `source-edit.test.ts`.
 */
export const WRITER_PAGE = 6;

/**
 * Что шлёт эта сборка. Поднят до `WRITER_REBASE` вместе с вкладкой, которая
 * умеет вливание (шаг 1в: `applyServerSlides` в `apps/web/.../plan-slides.ts`),
 * и не раньше: иначе сервер перебазировал бы сохранение вкладки, которая
 * дописанного не покажет. До `WRITER_PAGE` поднят тем же правилом (выше).
 */
export const PLAN_WRITER_FORMAT = WRITER_PAGE;

/**
 * Задание импорта, которое прямо сейчас дописывает страницы PDF в эту заготовку
 * (`docs/pdf-append-design.md`, 3.4 п. 7). Одна схема на `GET` заготовки и на
 * догоняющее чтение: из неё полоса берёт ход, ожидание очереди и резерв.
 *
 * `pages` это число выбранных страниц, `state` только `queued` или `running`
 * (брошенный экран выбора полосы хода не держит), `queueAhead` считается по
 * моменту выбора страниц.
 */
export const PlanAppending = z.object({
  jobId: z.string().uuid(),
  fileName: z.string(),
  pages: z.number().int().nonnegative(),
  donePages: z.number().int().nonnegative(),
  state: z.enum(["queued", "running"]),
  queueAhead: z.number().int().nonnegative(),
});
export type PlanAppending = z.infer<typeof PlanAppending>;

/**
 * Дописанное одной записью журнала: какие слайды встали и из какого PDF.
 *
 * `jobId` бывает `null` у записи, заведённой руками (оператор рубежа 1в); имя
 * файла пишется в запись при дописывании, потому что строка задания может уйти
 * каскадом вместе с исходником.
 */
export const AppendedGroup = z.object({
  jobId: z.string().uuid().nullable(),
  fileName: z.string(),
  slideIds: z.array(z.string()),
});
export type AppendedGroup = z.infer<typeof AppendedGroup>;

export const Plan = PlanSummary.extend({
  format: z.number().int(),
  slides: z.array(SlideSnapshot),
  /** `default({})` держит совместимость с сервером, который поля ещё не отдаёт. */
  sources: PlanSources.default({}),
  /**
   * Задания, дописывающие сюда страницы. `default([])` держит совместимость с
   * сервером, который поля ещё не отдаёт; заполняется с шага 2а.
   */
  appending: z.array(PlanAppending).default([]),
});
export type Plan = z.infer<typeof Plan>;

/**
 * Ответ сохранения. Если под вкладкой дописывали страницы, сервер
 * перебазировал её сохранение поверх них, и дописанное едет здесь: вкладка
 * вливает `appendedSlides` и только вместе с ними сдвигает ревизию (3.4 п. 8).
 * Без дописанного оба списка пустые.
 */
export const SavePlanSlidesResponse = PlanSummary.extend({
  appendedSlides: z.array(SlideSnapshot).default([]),
  appendedGroups: z.array(AppendedGroup).default([]),
});
export type SavePlanSlidesResponse = z.infer<typeof SavePlanSlidesResponse>;

/**
 * Догоняющее чтение `GET /v1/plans/:planId/appends?since=N`, без записи.
 *
 * Промежуток `(since, revision]` покрыт одними дописываниями: едут дописанные
 * слайды группами по записям. Не покрыт (правка в другом окне, снятая обрезкой
 * запись, `since` впереди сервера): `stale: true` без слайдов, и вкладка
 * предлагает перечитать заготовку. Ревизия на сервере не двигается.
 */
export const PlanAppendsResponse = z.discriminatedUnion("stale", [
  z.object({
    stale: z.literal(false),
    revision: z.number().int().nonnegative(),
    slides: z.array(SlideSnapshot),
    appendedGroups: z.array(AppendedGroup),
    appending: z.array(PlanAppending),
  }),
  z.object({
    stale: z.literal(true),
    revision: z.number().int().nonnegative(),
    appending: z.array(PlanAppending),
  }),
]);
export type PlanAppendsResponse = z.infer<typeof PlanAppendsResponse>;

/**
 * Корзина приезжает вместе со списком, а не отдельной ручкой: заготовок у
 * человека десятки, а не тысячи, и второй круг по сети при 128 кбит/с стоит
 * дороже, чем несколько лишних строк в ответе.
 */
export const PlanListResponse = z.object({
  plans: z.array(PlanSummary),
  trash: z.array(PlanSummary),
});
export type PlanListResponse = z.infer<typeof PlanListResponse>;

/**
 * Уроки, собранные по одной заготовке.
 *
 * Сама заготовка едет вместе со списком, и это не удобство, а экономия круга по
 * сети: экрану нужны её название для хлебных крошек и ссылка на редактор, а
 * второй запрос за ними на канале 128 кбит/с виден глазом.
 *
 * Уроки той же схемой `Lesson`, что и в кабинете: это те же занятия, показанные
 * с другой стороны, и вторая форма записи одного и того же однажды разъехалась
 * бы с первой.
 */
export const PlanLessonsResponse = z.object({
  plan: PlanSummary,
  lessons: z.array(Lesson),
});
export type PlanLessonsResponse = z.infer<typeof PlanLessonsResponse>;

/**
 * Пустой слайд. Живёт здесь, потому что заводят его двое: сервер при создании
 * заготовки и редактор по кнопке «добавить». Разъехавшиеся литералы означали бы
 * слайд без подложки, который не нарисуется.
 */
export function emptyPlanSlide(id: string): SlideSnapshot {
  return { id, background: EMPTY_BACKGROUND, elements: [] };
}

/**
 * Состояние урока относительно заготовки, из которой он собран.
 *
 * Подробности и переходы в docs/lesson-plan-link-design.md, раздел 5. Коротко:
 *
 * - `none` заготовки у урока нет вовсе, обновлять нечего
 * - `pending` материал ещё не взят. Правки заготовки доезжают до урока сами,
 *   потому что он возьмёт её при первом открытии доски
 * - `clean` материал взят, доска совпадает с тем, что было взято. Обновление
 *   без потерь
 * - `diverged` на доске что-то изменилось после взятия. Обновление с потерей, и
 *   потеря названа поимённо в `changes`
 * - `closed` ученик заходил или урок завершён. Обновления нет вовсе: переписать
 *   доску проведённого занятия нельзя ни при каком подтверждении
 */
export const LESSON_PLAN_STATES = ["none", "pending", "clean", "diverged", "closed"] as const;
export const LessonPlanState = z.enum(LESSON_PLAN_STATES);
export type LessonPlanState = z.infer<typeof LessonPlanState>;

/**
 * Можно ли обновить урок из заготовки по его состоянию. `taken` это «материал
 * взят, а сверку с доской не делали» (`readPlanLinkHead` на сервере, итог
 * дописывания страниц PDF, `docs/pdf-append-design.md`, 3.6): обновить такой
 * урок можно так же, как `clean` и `diverged`. `pending` сюда не входит: урок
 * возьмёт материал сам при первом открытии доски, и кнопку для него
 * добавляет только экран урока.
 */
export function planLinkUpdatable(state: LessonPlanState | "taken"): boolean {
  return state === "taken" || state === "clean" || state === "diverged";
}

/** Что на доске урока разошлось с взятым материалом. Считается по двум отпечаткам. */
export const LessonPlanChanges = z.object({
  /** Номера слайдов ТЕКУЩЕЙ доски: человек видит перед собой именно её. */
  changedSlides: z.array(z.number().int().positive()),
  addedSlides: z.number().int().nonnegative(),
  removedSlides: z.number().int().nonnegative(),
  answers: z.number().int().nonnegative(),
});
export type LessonPlanChanges = z.infer<typeof LessonPlanChanges>;

export const LessonPlanLink = z.object({
  lessonId: z.string().uuid(),
  planId: z.string().uuid().nullable(),
  /** Название заготовки. `null`, если её больше нет или урок собран без неё. */
  planTitle: z.string().nullable(),
  /** Заготовка в корзине: материал ещё есть, но человеку это стоит сказать. */
  planTrashed: z.boolean(),
  /**
   * Заготовка исчезла физически (ночная уборка корзины), а материал урок взять
   * успел. Отличается от «урок без заготовки» именно тем, что отметка стоит.
   */
  planGone: z.boolean(),
  /** Текущая ревизия заготовки и та, что была взята. Обе `null` до материализации. */
  planRevision: z.number().int().nullable(),
  takenRevision: z.number().int().nullable(),
  materializedAt: z.number().int().nullable(),
  state: LessonPlanState,
  /** Заготовку правили после того, как урок взял материал. */
  behind: z.boolean(),
  /** Есть только у `diverged`. Показ оптимистичен: считается по снимку доски. */
  changes: LessonPlanChanges.nullish(),
});
export type LessonPlanLink = z.infer<typeof LessonPlanLink>;

/**
 * Обновить урок из заготовки.
 *
 * `force` это подтверждение человека, увидевшего список потерь. Оно снимает
 * сверку с живым документом и не снимает двух других рубежей: занятый урок и
 * начатый урок не обновляются ни при каком подтверждении.
 */
export const SyncLessonPlanRequest = z.object({ force: z.boolean().optional() });
export type SyncLessonPlanRequest = z.infer<typeof SyncLessonPlanRequest>;

export const CreatePlanRequest = z.object({ title: PlanTitle });
export type CreatePlanRequest = z.infer<typeof CreatePlanRequest>;

export const RenamePlanRequest = z.object({ title: PlanTitle });
export type RenamePlanRequest = z.infer<typeof RenamePlanRequest>;

/**
 * Автосохранение редактора.
 *
 * ⚠️ `baseRevision` обязателен по смыслу с шага contract
 * (`docs/pdf-append-design.md`, 3.4 и шаг 1а): сохранение без него сервер не
 * пишет. В схеме поле осталось необязательным НАМЕРЕННО, и это не недосмотр.
 * Отказ схемы это `400 invalid_body`, и его получил бы любой, включая чужого;
 * правило проекта требует, чтобы чужой получал `404`, а свой понятный диагноз.
 * Поэтому отсутствие ревизии разбирает `savePlanSlides` ДО записи: чтением с
 * предикатом владельца, `404 plan_not_found` либо `409 plan_writer_outdated`
 * (вкладка без ревизии это бандл старше expand, ей нужно перечитать сайт).
 *
 * Отказ стоит до запроса, а не предикатом в нём: стой он в `UPDATE`, ноль
 * строк не отличал бы «без ревизии» от «чужая» и «устарела», а без отказа
 * вовсе сохранение без ревизии прошло бы поверх дописанных страниц.
 */
export const SavePlanSlidesRequest = z.object({
  slides: PlanSlidesWire,
  baseRevision: z.number().int().nonnegative().optional(),
  /**
   * Источники упражнений, которые редактор считает своими. Сервер по ним
   * ПЕРЕСОБИРАЕТ элементы слоя 2 в присланных слайдах, а не верит присланному:
   * компиляция это единственное место, где ответ отделяется от условия, и
   * доверять её клиенту значит не иметь её вовсе.
   *
   * Необязательно: вкладка без второго слоя ничего не шлёт, и упражнений у неё
   * тоже нет.
   */
  sources: PlanSources.optional(),
  /**
   * Что умеет эта вкладка (см. `PLAN_WRITER_FORMAT`). Необязательно, это шаг
   * expand: старый бандл поля не шлёт, и отказывать ему на заготовке БЕЗ
   * упражнений нельзя, он ничего не портит.
   */
  writerFormat: z.number().int().nonnegative().optional(),
});
export type SavePlanSlidesRequest = z.infer<typeof SavePlanSlidesRequest>;
